import { Observable, Subject } from 'rxjs';
import { Injectable } from '@angular/core';

import { AccountService } from './account.service';
import { LoginService } from './login.service';
import { ProfileService } from './profile.service';
import { TokenService } from './token.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private initialized = false;
  private loggedIn = false;
  private _isAuthenticated: Subject<boolean> = new Subject();

  get isAuthenticated(): Observable<boolean> {
    return this._isAuthenticated.asObservable();
  }

  constructor(
    private loginService: LoginService,
    private tokenService: TokenService,
    private accountService: AccountService,
    private profileService: ProfileService
  ) {}

  /**
   * Checks user is logged, true if he's logged in, false otherwise
   */
  public isLoggedIn(): boolean {
    return this.loggedIn;
  }

  /**
   * Authenticates user
   * @param isAuthenticated
   */
  public authenticate(isAuthenticated: boolean): void {
    this.loggedIn = isAuthenticated;
    this._isAuthenticated.next(isAuthenticated);
  }

  /**
   * Checks initialization of authentication,
   * true if we already called the initialize() method during this session
   * @returns initialization of authentication
   */
  public isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Checks if there is a token stored in the browser and tries to retrieve the account.
   * This method is only called once, to check if we are already logged in upon loading the first page.
   * @returns initialization end
   */
  public initialize(): Promise<any> {
    return new Promise<void>((resolve, reject) => {
      if (!this.initialized) {
        this.initialized = true;
        this.retrieveAccount().then(resolve).catch(resolve);
      } else {
        resolve();
      }
    });
  }

  /**
   * Retrieves an authentication token from the specified credentials.
   * @param username
   * @param password
   * @returns user login
   */
  public retrieveToken(
    username: string,
    password: string
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.loginService.authenticate(username, password).subscribe({
        next: (res) => {
          this.tokenService.save(res.refreshToken, res.accessToken);
          resolve(res);
        },
        error: (err: any) => reject(err)
      });
    });
  }

  /**
   * Tries to retrieve the account information from our authentication token.
   * @returns account set is finished
   */
  public retrieveAccount(): Promise<any> {
    return new Promise<void>((resolve, reject) => {
      if (this.tokenService.hasToken()) {
        this.accountService.getMe().then(
          (data: any) => {
            this.accountSuccess(data);
            this.authenticate(true);
            resolve();
          },
          (err: any) => {
            this.accountFailure(err);
            this.authenticate(false);
            reject();
          }
        );
      } else {
        this.authenticate(false);
        reject();
      }
    });
  }

  /**
   * Clears the authentication and deletes the token from the storage.
   */
  public logout(): void {
    this.tokenService.clear();
    this.profileService.me = null;

    this.authenticate(false);
  }

  /**
   * Callback executed when we managed to retrieve our account from the token.
   * @param result
   */
  private accountSuccess(result: any): void {
    this.profileService.me = result;
  }

  /**
   * Callback executed when we could not retrieve the account information.
   * This can happen if the server is down or if our authentication token expired.
   * @param error
   */
  private accountFailure(error: any): void {
    // The token is probably invalid, erase it from the storage.
    this.tokenService.clear();
  }
}
